import React, { useEffect, useState } from 'react';
import { Badge, Button, Card, Col, Container, Row } from 'react-bootstrap';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import QuestionsList from '../../../../Individual/Components/ChecklistComponents/QuestionsList';

const BASE_URL = process.env.REACT_APP_API_URL;


// Add this component before the main AuditSections component
const CircularProgress = ({ percentage, size = 80, showLabel = true }) => {
  const strokeWidth = 8;
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const offset = circumference - (percentage / 100) * circumference;

  // Get color based on percentage
  const getProgressColor = (percent) => {
    if (percent >= 100) return '#198754'; // success/green
    if (percent >= 75) return '#0d6efd';  // primary/blue
    if (percent >= 50) return '#ffc107';  // warning/yellow
    if (percent >= 25) return '#fd7e14';  // orange
    return '#dc3545';                      // danger/red
  };

  const progressColor = getProgressColor(percentage);

  return (
    <div className="circular-progress" style={{ width: size, height: size }}>
      <svg width={size} height={size}>
        <circle
          className="progress-bg"
          stroke="#e9ecef"
          strokeWidth={strokeWidth}
          fill="none"
          r={radius}
          cx={size / 2}
          cy={size / 2}
        />
        <circle
          className="progress-bar"
          stroke={progressColor}
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          fill="none"
          r={radius}
          cx={size / 2}
          cy={size / 2}
        />
        {showLabel && (
          <text
            x="50%"
            y="50%"
            dy=".1em"
            textAnchor="middle"
            dominantBaseline="middle"
            className="progress-text-large"
            style={{ fill: progressColor }}
          >
            {percentage}%
          </text>
        )}
      </svg>
    </div>
  );
};

const BusinessAuditSections = () => {
  const [sectionNames, setSectionNames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSection, setSelectedSection] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [subsections, setSubsections] = useState([]);
  const [currentPath, setCurrentPath] = useState([]);
  const [answers, setAnswers] = useState({});
  const [dataTable, setDataTable] = useState({
    totalQuestions: 0,
    answeredQuestions: 0,
    unansweredQuestions: 0,
    totalPoints: 0,
    maxPossiblePoints: 0,
    percentage: 0
  });
  const [showNext, setShowNext] = useState(false);
  const [currentLevel, setCurrentLevel] = useState('section');
  const [sectionData, setSectionData] = useState(null);
  const [sectionProgress, setSectionProgress] = useState(null);
  const [sectionsProgress, setSectionsProgress] = useState({});
  const [subsectionProgress, setSubsectionProgress] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [isSectionLoading, setIsSectionLoading] = useState(false);
  const [isViewOnly, setIsViewOnly] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { checklist } = location.state || {};
  const auditorData = JSON.parse(localStorage.getItem("auditor"));
  const auditorId = auditorData?.id || null;

  const notify = (message, type = 'error') => toast[type](message, {
    position: "top-right",
    autoClose: 3000
  });

  const calculatePoints = (question, answer) => {
    if (!question.optionPoints) return 0;
    
    try {
      const points = question.optionPoints.split(',').map(p => Number(p.trim()));
      const options = question.answerOptions.split(',').map(opt => opt.trim());
      
      if (Array.isArray(answer)) {
        // For multiple choice questions
        return answer.reduce((total, ans) => {
          const index = options.indexOf(ans);
          return total + (index !== -1 ? points[index] : 0);
        }, 0);
      } else {
        // For single choice questions
        const index = options.indexOf(answer);
        return index !== -1 ? points[index] : 0;
      }
    } catch (error) {
      console.error('Error calculating points:', error);
      return 0;
    }
  };

  useEffect(() => {
    const fetchSections = async () => {
      if (!checklist?.checklist_id || !checklist?.CompanyCode) {
        notify("Missing checklist information");
        setLoading(false);
        return;
      }

      try {
        const url = `${BASE_URL}/get-business-checklist-sections/${checklist.checklist_id}/${checklist.CompanyCode}`;
        const response = await fetch(url);

        if (!response.ok) {
          throw new Error(`Failed to fetch sections: ${response.status}`);
        }

        const data = await response.json();
        if (!data.sections) {
          throw new Error('No sections data in response');
        }

        setSectionNames(data.sections);
        
        // Set view-only mode if checklist is completed
        setIsViewOnly(checklist.status === 'Completed');
        
      } catch (error) {
        console.error('Fetch error:', error);
        notify(error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchSections();
  }, [checklist]);

const fetchSectionProgress = async (path = []) => {
  try {
    const pathParam = path.length > 0 ? path.join('|') : '';
    const response = await fetch(
      `${BASE_URL}/get-business-section-progress/${checklist.checklist_id}/${checklist.CompanyCode}/${auditorData.id}/${checklist.branch_id}${pathParam ? `/${pathParam}` : ''}`
    );

    if (!response.ok) throw new Error('Failed to fetch progress');
    
    const data = await response.json();
    setSectionProgress(data);
  } catch (error) {
    console.error('Error:', error);
    notify('Failed to load progress data', 'error');
  }
};

const fetchAllSectionsProgress = async () => {
  try {
    const progressMap = {};

    // Process each section recursively
    const processSection = async (path) => {
      const pathString = path.join('|');
      
      // Fetch progress for current path
      const progressResponse = await fetch(
        `${BASE_URL}/get-business-section-progress/${checklist.checklist_id}/${checklist.CompanyCode}/${auditorData.id}/${checklist.branch_id}/${pathString}`
      );
      
      if (!progressResponse.ok) throw new Error('Failed to fetch progress');
      const progressData = await progressResponse.json();
      progressMap[pathString] = progressData;

      // Fetch section details to check for subsections
      const detailsResponse = await fetch(
        `${BASE_URL}/get-business-nested-section-details/${checklist.checklist_id}/${checklist.CompanyCode}/${pathString}`
      );
      
      if (!detailsResponse.ok) throw new Error('Failed to fetch section details');
      const sectionData = await detailsResponse.json();

      // Recursively process subsections if they exist
      if (sectionData.subsections?.length > 0) {
        for (const subsection of sectionData.subsections) {
          await processSection([...path, subsection.name]);
        }
      }
    };

    // Start processing from main sections
    for (const section of sectionNames) {
      await processSection([section]);
    }

    setSectionsProgress(progressMap);
  } catch (error) {
    console.error('Error fetching progress:', error);
    notify('Failed to load progress data');
  }
};

 const fetchSubsectionProgress = async (subsectionPath) => {
  try {
    // Ensure subsectionPath is properly formatted
    const pathString = Array.isArray(subsectionPath) 
      ? subsectionPath.join('|')
      : subsectionPath;

    // First fetch progress for the current path
    const progressResponse = await fetch(
      `${BASE_URL}/get-business-section-progress/${checklist.checklist_id}/${checklist.CompanyCode}/${auditorData.id}/${checklist.branch_id}/${pathString}`
    );

    if (!progressResponse.ok) {
      throw new Error(`Failed to fetch progress for path: ${pathString}`);
    }

    const progressData = await progressResponse.json();
    
    // Update progress state for current path
    setSubsectionProgress(prev => ({
      ...prev,
      [pathString]: progressData
    }));

    // Then check for nested subsections
    const detailsResponse = await fetch(
      `${BASE_URL}/get-business-nested-section-details/${checklist.checklist_id}/${checklist.CompanyCode}/${pathString}`
    );

    if (detailsResponse.ok) {
      const details = await detailsResponse.json();
      
      // If there are nested subsections, fetch their progress recursively
      if (details.subsections?.length > 0) {
        await Promise.all(details.subsections.map(async (nestedSubsection) => {
          const nestedPath = Array.isArray(subsectionPath)
            ? [...subsectionPath, nestedSubsection.name]
            : `${pathString}|${nestedSubsection.name}`;
            
          try {
            const nestedProgressResponse = await fetch(
              `${BASE_URL}/get-business-section-progress/${checklist.checklist_id}/${checklist.CompanyCode}/${auditorData.id}/${checklist.branch_id}/${Array.isArray(nestedPath) ? nestedPath.join('|') : nestedPath}`
            );

            if (nestedProgressResponse.ok) {
              const nestedProgressData = await nestedProgressResponse.json();
              setSubsectionProgress(prev => ({
                ...prev,
                [Array.isArray(nestedPath) ? nestedPath.join('|') : nestedPath]: nestedProgressData
              }));
            }
          } catch (nestedError) {
            console.error(`Error fetching nested progress for ${nestedSubsection.name}:`, nestedError);
          }
        }));
      }
    }

    return progressData;
  } catch (error) {
    console.error('Error fetching subsection progress:', error);
    // Don't throw the error, just log it and return null
    return null;
  }
};

  useEffect(() => {
    const fetchProgress = async () => {
      if (subsections.length > 0 && currentPath.length > 0) {
        try {
          // Fetch progress for all subsections in parallel
          await Promise.all(subsections.map(async (subsection) => {
            const subsectionPath = [...currentPath, subsection.name];
            await fetchSubsectionProgress(subsectionPath);
          }));
        } catch (error) {
          console.error('Error fetching progress:', error);
        }
      }
    };

    fetchProgress();
  }, [subsections, currentPath, checklist]);

  const loadSavedAnswers = async (sectionName) => {
    try {
      const response = await fetch(
        `${BASE_URL}/get-business-answers/${checklist.checklist_id}/${checklist.id}/${checklist.branch_id}`
      );
      
      if (!response.ok) {
        throw new Error('Failed to fetch saved answers');
      }
      
      const data = await response.json();
      
      // Filter answers for current section if provided
      const relevantAnswers = sectionName 
        ? Object.fromEntries(
            Object.entries(data.data).filter(([_, answer]) => 
              answer.section === sectionName
            )
          )
        : data.data;

      // Update answers state with saved data
      setAnswers(prev => ({
        ...prev,
        ...relevantAnswers
      }));
      
      // Update progress tracking
      const answeredCount = Object.values(relevantAnswers).filter(a => a.answer).length;
      setDataTable(prev => ({
        ...prev,
        answeredQuestions: answeredCount,
        unansweredQuestions: questions.length - answeredCount
      }));

    } catch (error) {
      console.error('Error loading saved answers:', error);
      notify('Failed to load saved answers');
    }
  };

  useEffect(() => {
    if (checklist?.checklist_id && checklist?.id && checklist?.branch_id) {
      loadSavedAnswers();
    }
  }, [checklist?.checklist_id, checklist?.id, checklist?.branch_id]);

  useEffect(() => {
    if (sectionNames.length > 0) {
      fetchAllSectionsProgress();
    }
  }, [sectionNames]);

  const handleSectionClick = async (sectionName, index) => {
    setSelectedSection(sectionName);
    setIsSectionLoading(true);
    setCurrentPath([sectionName]);
    setShowNext(false);
    setCurrentLevel('section');

    try {
      const response = await fetch(
        `${BASE_URL}/get-business-section-details/${checklist.checklist_id}/${checklist.CompanyCode}/${sectionName}`
      );

      if (!response.ok) {
        throw new Error('Failed to fetch section details');
      }

      const data = await response.json();
      setSectionData(data);
      
      if (data.type === 'questions' || data.type === 'both') {
        setQuestions(data.type === 'both' ? data.questions : data.data);
        setSubsections(data.type === 'both' ? data.subsections : []);
        setShowNext(data.type === 'both');
      } else if (data.type === 'subsections') {
        setQuestions([]);
        setSubsections(data.data);
      } else {
        notify('No content found for this section', 'info');
      }

      await loadSavedAnswers(sectionName);
      await fetchSectionProgress([sectionName]);
    } catch (error) {
      console.error('Error:', error);
      notify(error.message);
    } finally {
      setIsSectionLoading(false);
    }
  };

  const handleItemClick = async (itemName, itemType, parentPath = []) => {
    setIsSectionLoading(true);
    const newPath = [...parentPath, itemName];
    setCurrentPath(newPath);
    setShowNext(false);
    
    try {
      const pathParam = newPath.join('|');

      const response = await fetch(
        `${BASE_URL}/get-business-nested-section-details/${checklist.checklist_id}/${checklist.CompanyCode}/${pathParam}`
      );

      if (!response.ok) {
        throw new Error('Failed to fetch details');
      }

      const data = await response.json();
      setSectionData(data);
      
      // Determine the next level based on current path length
      const nextLevel = newPath.length === 1 ? 'subsection' : 
                       newPath.length === 2 ? 'subsubsection' : 
                       'final';
      setCurrentLevel(nextLevel);

      if (data.type === 'questions' || data.type === 'both') {
        setQuestions(data.type === 'both' ? data.questions : data.data);
        if (data.type === 'both' && data.subsections?.length > 0) {
          setShowNext(true);
          setSubsections(data.subsections);
        } else {
          setShowNext(false);
          setSubsections([]);
        }
      } else if (data.type === 'subsections') {
        setQuestions([]);
        setSubsections(data.data);
        setShowNext(false);
      } else {
        setQuestions([]);
        setSubsections([]);
        notify('No content found', 'info');
      }
      
      await loadSavedAnswers(newPath[0]);
      await fetchSectionProgress(newPath);
    } catch (error) {
      console.error('Error:', error);
      notify(error.message);
    } finally {
      setIsSectionLoading(false);
    }
  };

  const navigateToPath = (index) => {
    // Navigate to a specific point in the path
    const newPath = currentPath.slice(0, index + 1);
    handleItemClick(newPath[newPath.length - 1], 'section', newPath.slice(0, -1));
  };

  const handleAnswerChange = (questionId, changes) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: {
        ...prev[questionId],
        ...changes,
        section: currentPath[0] // Always include top-level section
      }
    }));

    // Update progress tracking
    setDataTable(prev => {
      const answeredCount = Object.values(answers).filter(a => a.answer).length;
      return {
        ...prev,
        answeredQuestions: answeredCount,
        unansweredQuestions: prev.totalQuestions - answeredCount
      };
    });
  };

  const toastConfig = {
    position: "top-right",
    autoClose: 2000,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
  };

  const handleSubmit = async (shouldNavigate = false) => {
    setIsSaving(true);
    try {
      const formData = new FormData();
      const answersToSubmit = [];
  
      Object.entries(answers).forEach(([questionId, answerData]) => {
        if (!answerData?.answer?.trim()) return;
  
        const answerPayload = {
          questionId,
          section: currentPath[0],
          subsection: currentPath[1],
          subsubsection: currentPath[2],
          answer: answerData.answer,
          remarks: answerData.remarks || '',
          points: answerData.points || 0,
          cap: answerData.cap,
          mediaFileNames: [] // Initialize array for media files
        };
  
        // Handle media files
        if (answerData.mediaFiles?.length > 0) {
          answerData.mediaFiles.forEach(file => {
            if (file.file instanceof File) {
              // For new files
              formData.append('media', file.file);
              answerPayload.mediaFileNames.push(file.file.name);
            } else if (file.filename) {
              // For existing files
              answerPayload.mediaFileNames.push(file.filename);
            }
          });
        } else {
          // Explicitly set mediaFileNames to empty array when no files
          answerPayload.mediaFileNames = [];
          
          // Add a flag to indicate all media files were removed
          answerPayload.clearMedia = true;
        }
  
        // Add deleted files if any
        if (answerData.deletedFiles?.length > 0) {
          answerPayload.deletedFiles = answerData.deletedFiles;
        }
  
        answersToSubmit.push(answerPayload);
      });
  
      formData.append('answers', JSON.stringify(answersToSubmit));
      formData.append('checklistId', checklist.checklist_id);
      formData.append('auditorId', auditorId);
      formData.append('branchId', checklist.branch_id);
  
      const response = await fetch(`${BASE_URL}/save-business-answers`, {
        method: 'POST',
        body: formData
      });
  
      const result = await response.json();
  
      if (!response.ok) {
        throw new Error(result.error || 'Failed to save answers');
      }
  
      // Update media files in state with saved filenames
      setAnswers(prev => {
        const updated = { ...prev };
        result.savedFiles.forEach(savedFile => {
          Object.keys(updated).forEach(questionId => {
            if (updated[questionId].mediaFiles) {
              updated[questionId].mediaFiles = updated[questionId].mediaFiles.map(file => {
                if (file.file instanceof File && file.file.name === savedFile.originalName) {
                  return {
                    filename: savedFile,
                    name: file.file.name,
                    isNew: false
                  };
                }
                return file;
              });
            }
          });
        });
        return updated;
      });
  
      // Show success toast with conditional navigation
      if (shouldNavigate) {
        toast.success("Answers saved successfully!", {
          ...toastConfig,
          onClose: () => navigate('/BusinessAuditDetails', { 
            state: { 
              checklist,
              from: 'sections',
              sectionPath: currentPath
            }
          })
        });
      } else {
        toast.success("Progress saved successfully!", toastConfig);
      }
  
    } catch (error) {
      console.error('Error:', error);
      toast.error(error.message, toastConfig);
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveProgress = () => {
    handleSubmit(false); // Save without navigation
  };

  const handleSaveAndContinue = () => {
    handleSubmit(true); // Save with navigation
  };

  const resetNavigation = () => {
    setSelectedSection(null);
    setSubsections([]);
    setQuestions([]);
    setCurrentPath([]);
  };

  const handleNext = () => {
    if (sectionData?.subsections?.length > 0) {
      setQuestions([]);
      setSubsections(sectionData.subsections);
      setShowNext(false);
      setCurrentLevel(prevLevel => {
        switch (prevLevel) {
          case 'section':
            return 'subsection';
          case 'subsection':
            return 'subsubsection';
          default:
            return prevLevel;
        }
      });
    }
  };

  const ProgressIndicator = ({ progress }) => {
    // Only show progress bar for main sections
    if (!progress || progress.level !== 'section') return null;

    return (
      <div className="section-progress mb-4">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <h6 className="text-muted text-uppercase small mb-0">Section Progress</h6>
          <span className="badge bg-primary">{progress.progressPercentage}%</span>
        </div>
        <div className="progress" style={{ height: '10px' }}>
          <div 
            className="progress-bar" 
            role="progressbar" 
            style={{ width: `${progress.progressPercentage}%` }}
            aria-valuenow={progress.progressPercentage} 
            aria-valuemin="0" 
            aria-valuemax="100"
          />
        </div>
        <div className="d-flex justify-content-between mt-2 small text-muted">
          <span>{progress.answeredQuestions} answered</span>
          <span>{progress.totalQuestions} total</span>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <Container fluid className="py-5">
        <div className="text-center py-5">
          <div className="spinner-grow text-primary mb-3" style={{ width: '3rem', height: '3rem' }} role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="text-primary fw-bold mb-1">Loading Audit Sections</p>
          <p className="text-muted">Please wait while we fetch your section data...</p>
        </div>
      </Container>
    );
  }

  return (
    <Container fluid className={`py-4 ${isViewOnly ? 'view-only' : ''}`}>
      <ToastContainer />
      
      <Row className="mb-4">
        <Col>
          <div className="d-flex justify-content-between align-items-start">
            <div>
              <h3 className="mb-2">{checklist.checklist_name}</h3>
              {isViewOnly && (
                <div className="alert alert-info py-2 px-3 mb-2" style={{ fontSize: '0.875rem' }}>
                  <i className="fas fa-eye me-2"></i>
                  This checklist is completed and in view-only mode
                </div>
              )}
            </div>
            {isViewOnly && (
              <Badge bg="success" className="px-3 py-2">
                <i className="fas fa-check-circle me-1"></i>
                Completed
              </Badge>
            )}
          </div>
          <nav aria-label="breadcrumb">
            <ol className="breadcrumb">
              <li className="breadcrumb-item">
                <a href="#" onClick={(e) => {
                  e.preventDefault();
                  resetNavigation();
                }}>Sections</a>
              </li>
              
              {currentPath.map((pathItem, index) => (
                <li 
                  key={index} 
                  className={`breadcrumb-item ${index === currentPath.length - 1 ? 'active' : ''}`}
                >
                  {index === currentPath.length - 1 ? (
                    <span>{pathItem}</span>
                  ) : (
                    <a href="#" onClick={(e) => {
                      e.preventDefault();
                      navigateToPath(index);
                    }}>{pathItem}</a>
                  )}
                </li>
              ))}
              
              {questions.length > 0 && currentPath.length > 0 && (
                <li className="breadcrumb-item active">Questions</li>
              )}
            </ol>
          </nav>
        </Col>
      </Row>

      {sectionProgress && <ProgressIndicator progress={sectionProgress} />}

      {currentPath.length === 0 ? (
        <Row xs={1} md={2} lg={3} className="g-4">
          {sectionNames.map((sectionName, index) => {
            const progress = sectionsProgress[sectionName] || {
              progressPercentage: 0,
              answeredQuestions: 0,
              totalQuestions: 0
            };

            return (
              <Col key={index}>
                <Card 
                  className="h-100 shadow-sm hover-shadow"
                  onClick={() => handleSectionClick(sectionName, index)}
                  role="button"
                >
                  <Card.Body className="d-flex flex-column">
                    <div className="text-center mb-3">
                      <CircularProgress 
                        percentage={progress.progressPercentage}
                        size={80}
                        showLabel={true}
                      />
                    </div>
                    <div className="text-center mb-3">
                      <h5 className="mb-1">{sectionName}</h5>
                      <small className="text-muted d-block">
                        {progress.totalQuestions} Questions
                      </small>
                    </div>
                    <div className="mt-auto">
                      <div className="d-flex justify-content-center gap-3 mb-2">
                        <div className="text-success">
                          <small>{progress.answeredQuestions} Answered</small>
                        </div>
                        <div className="text-danger">
                          <small>{progress.totalQuestions - progress.answeredQuestions} Remaining</small>
                        </div>
                      </div>
                      <div className="text-center">
                        <small className={`badge bg-${
                          progress.progressPercentage === 100 ? 'success' :
                          progress.progressPercentage > 0 ? 'warning' : 'danger'
                        }`}>
                          {progress.progressPercentage === 100 ? 'Completed' :
                          progress.progressPercentage > 0 ? 'In Progress' : 'Not Started'}
                        </small>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            );
          })}
        </Row>
      ) : (
        <>
          {questions.length > 0 && (
            <>
              <QuestionsList
                questions={questions}
                answers={answers}
                handleAnswerChange={handleAnswerChange}
                setAnswers={setAnswers}
                selectedSection={currentPath[currentPath.length - 1]}
                handleSubmit={!showNext && !isViewOnly ? handleSaveAndContinue : undefined}
                checklist={checklist}
                auditorId={auditorId}
                isViewOnly={isViewOnly}
              />
            </>
          )}

          {subsections.length > 0 && !questions.length && (
            <Row xs={1} md={2} lg={3} className="g-4">
              {subsections.map((subsection, index) => {
                const subsectionPath = [...currentPath, subsection.name].join('|');
                const progress = subsectionProgress[subsectionPath] || {
                  progressPercentage: 0,
                  answeredQuestions: 0,
                  totalQuestions: 0
                };

                return (
                  <Col key={index}>
                    <Card 
                      className="h-100 shadow-sm hover-shadow"
                      onClick={() => handleItemClick(
                        subsection.name, 
                        currentLevel, 
                        currentPath
                      )}
                      role="button"
                    >
                      <Card.Body className="d-flex flex-column">
                        <div className="text-center mb-3">
                          <CircularProgress 
                            percentage={progress.progressPercentage} 
                            size={80}
                            showLabel={true}
                          />
                        </div>
                        <div className="text-center mb-3">
                          <h5 className="mb-1">{subsection.name}</h5>
                          <small className="text-muted d-block">
                            {progress.totalQuestions} Questions
                          </small>
                        </div>
                        <div className="mt-auto">
                          <div className="d-flex justify-content-center gap-3 mb-2">
                            <div className="text-success">
                              <small>{progress.answeredQuestions} Answered</small>
                            </div>
                            <div className="text-danger">
                              <small>{progress.totalQuestions - progress.answeredQuestions} Remaining</small>
                            </div>
                          </div>
                          <div className="text-center">
                            <small className={`badge bg-${
                              progress.progressPercentage === 100 ? 'success' :
                              progress.progressPercentage > 0 ? 'warning' : 'danger'
                            }`}>
                              {progress.progressPercentage === 100 ? 'Completed' :
                              progress.progressPercentage > 0 ? 'In Progress' : 'Not Started'}
                            </small>
                          </div>
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>
                );
              })}
            </Row>
          )}

          {!questions.length && !subsections.length && (
            <div className="text-center py-5">
              <i className="fas fa-folder-open fa-4x text-muted mb-3"></i>
              <h4 className="text-muted">No content found</h4>
              <Button 
                variant="outline-primary" 
                className="mt-3"
                onClick={resetNavigation}
              >
                Return to Sections
              </Button>
            </div>
          )}

          {(questions.length > 0 || subsections.length > 0) && (
            <div className="navigation-buttons d-flex justify-content-between mt-4">
              <Button 
                variant="outline-secondary"
                onClick={() => {
                  if (currentPath.length === 1) {
                    resetNavigation();
                  } else {
                    navigateToPath(currentPath.length - 2);
                  }
                }}
              >
                <i className="fas fa-arrow-left me-2"></i>
                Back
              </Button>

              <div className="d-flex gap-2">
                {questions.length > 0 && !isViewOnly && (
                  <Button 
                    variant="primary"
                    onClick={handleSaveProgress}
                    disabled={isSaving}
                    className="d-flex align-items-center"
                  >
                    {isSaving ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Saving...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-save me-2"></i>
                        Save Progress
                      </>
                    )}
                  </Button>
                )}

                {showNext && !isViewOnly && (
                  <Button 
                    variant="info"
                    onClick={handleNext}
                    className="d-flex align-items-center"
                  >
                    Next
                    <i className="fas fa-arrow-right ms-2"></i>
                  </Button>
                )}

                {!showNext && questions.length > 0 && !isViewOnly && (
                  <Button 
                    variant="success"
                    onClick={handleSaveAndContinue}
                    disabled={isSaving}
                    className="d-flex align-items-center"
                  >
                    {isSaving ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Saving...
                      </>
                    ) : (
                      <>
                        Save & Continue
                        <i className="fas fa-check ms-2"></i>
                      </>
                    )}
                  </Button>
                )}
              </div>
            </div>
          )}
        </>
      )}

      <style>
        {`
          .hover-shadow:hover {
            transform: translateY(-5px);
            cursor: pointer;
            transition: transform 0.2s ease-in-out;
          }
          .department-icon, .subsection-icon, .subsubsection-icon {
            width: 50px;
            height: 50px;
            border-radius: 10px;
            display: flex;
            align-items: center;
            justify-content: center;
            background-color: rgba(13, 110, 253, 0.1);
          }
          .subsection-icon, .subsubsection-icon {
            background-color: rgba(23, 162, 184, 0.1);
          }
          .breadcrumb-item a {
            text-decoration: none;
            color: #0d6efd;
          }
          .breadcrumb-item a:hover {
            text-decoration: underline;
          }
          .circular-progress {
            position: relative;
            margin-left: 1rem;
          }
          .progress-bg {
            transform: rotate(-90deg);
            transform-origin: 50% 50%;
          }
          .progress-bar {
            transform: rotate(-90deg);
            transform-origin: 50% 50%;
            transition: stroke-dashoffset 0.35s, stroke 0.35s;
          }
          .card:hover .circular-progress circle.progress-bar {
            filter: brightness(0.9);
          }
          .border-top {
            border-top: 1px solid rgba(0,0,0,.125) !important;
          }
          .card {
            transition: all 0.3s ease;
          }
          .card:hover {
            transform: translateY(-5px);
            box-shadow: 0 5px 15px rgba(0,0,0,0.1) !important;
          }
          .progress-text-large {
            font-size: 24px;
            font-weight: 700;
            transition: fill 0.35s;
          }
          .progress-text-small {
            font-size: 12px;
            fill: #6c757d;
          }
          .badge {
            font-weight: 500;
            padding: 0.5em 1em;
          }
          .navigation-buttons {
            position: sticky;
            bottom: 1rem;
            z-index: 100;
            padding: 1rem;
            background-color: rgba(255, 255, 255, 0.9);
            backdrop-filter: blur(8px);
            border-radius: 0.5rem;
            box-shadow: 0 2px 10px rgba(0,0,0,0.1);
          }
          @media (max-width: 576px) {
            .navigation-buttons {
              position: fixed;
              bottom: 0;
              left: 0;
              right: 0;
              border-radius: 0;
              border-top: 1px solid rgba(0,0,0,0.1);
            }
          }
          /* Enhanced loading animation */
          .spinner-grow {
            animation-duration: 1.5s;
          }
          
          .text-primary.fw-bold {
            font-size: 1.2rem;
          }
          
          /* Fadeout animation for when loading completes */
          @keyframes fadeOut {
            from { opacity: 1; }
            to { opacity: 0; }
          }
          
          .loading-fadeout {
            animation: fadeOut 0.5s ease-out forwards;
          }

          /* View-only mode styles */
          .view-only input,
          .view-only textarea,
          .view-only select,
          .view-only .form-control {
            pointer-events: none;
            background-color: #f8f9fa !important;
            border-color: #dee2e6 !important;
            cursor: not-allowed;
          }

          .view-only .btn-primary,
          .view-only .btn-success,
          .view-only .btn-info {
            display: none;
          }

          .view-only .file-upload-area,
          .view-only .file-upload-button {
            pointer-events: none;
            opacity: 0.6;
            background-color: #f8f9fa !important;
          }

          .view-only .btn-outline-danger {
            display: none;
          }

          .view-only .form-check-input {
            pointer-events: none;
            background-color: #f8f9fa !important;
          }

          .view-only .form-range {
            pointer-events: none;
            opacity: 0.6;
          }

          .view-only .image-preview .btn {
            display: none;
          }

          .view-only .media-preview .btn {
            display: none;
          }

          .view-only .remarks-section textarea {
            pointer-events: none;
            background-color: #f8f9fa !important;
          }

          .view-only .points-display {
            opacity: 0.8;
          }

          /* Keep navigation buttons functional */
          .view-only .navigation-buttons .btn-outline-secondary {
            pointer-events: auto;
            opacity: 1;
            background-color: transparent !important;
          }

          /* Alert styling for view-only notification */
          .alert-info {
            border-left: 4px solid #0dcaf0;
            background-color: #cff4fc;
            border-color: #b8e6f0;
            color: #055160;
          }

          .view-only .progress-text {
            opacity: 0.8;
          }

          .view-only .card {
            background-color: #f8f9fa;
            border-color: #dee2e6;
          }

          .view-only .section-progress {
            opacity: 0.8;
          }

          /* Disable hover effects in view-only mode */
          .view-only .hover-shadow:hover {
            transform: none !important;
            box-shadow: 0 .125rem .25rem rgba(0,0,0,.075) !important;
          }

          .view-only .card:hover {
            transform: none !important;
            box-shadow: 0 .125rem .25rem rgba(0,0,0,.075) !important;
          }

          /* Make completed badge more prominent */
          .badge.bg-success {
            font-size: 0.875rem;
            padding: 0.5rem 1rem;
          }
        `}
      </style>
    </Container>
  );
};

export default BusinessAuditSections;